/**
 * RenderActions
 *
 * Per-record actions in the communication log's table row. Consumer:
 * `communicationColumns`.
 *
 * Every control is always rendered and disabled when it cannot act, never hidden,
 * and every disabled control carries a Tooltip naming the reason. Edit and Delete
 * go grey on a read-only log; the Signify status check does not, because it is a
 * pure read and a read-only log — where a stale `pending` attempt sits — is exactly
 * where it is needed. It is enabled only for a `createOrder` record whose outcome
 * is `pending`: the lookup searches Signify's orders, so a quote submit has nothing
 * to find there, and `pending` is the only outcome an observation can inform. It
 * writes nothing to the record, so the row's tag is unchanged when the report closes.
 * The label picker follows the same rule and goes grey on a read-only log, and again
 * where the organization has defined no communication log label to offer.
 *
 * `aria-label`s carry the record id: a twenty-row log would otherwise offer twenty
 * indistinguishable controls.
 */

import { FC, useState } from 'react';
import { Button, Popconfirm, Checkbox, Popover, Modal, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined, QuestionCircleOutlined, SearchOutlined } from '@ant-design/icons';
import type { Snapshot } from 'valtio';
import FlexBox from '../../../../atoms/FlexBox';
import FlexCol from '../../../../atoms/FlexCol';
import { CommunicationRecord, LabelId, LabelSet, SignifyOrderStatusCheck } from '#types';
import CommunicationLabelPicker from './CommunicationLabelPicker';
import EmailPopoverForm, { EmailFormData } from './EmailPopoverForm';
import PhoneCallPopoverForm, { PhoneCallFormData } from './PhoneCallPopoverForm';
import SignifyOrderStatusReport from './SignifyOrderStatusReport';

interface RenderActionsProps {
    record: CommunicationRecord;
    disabled?: boolean;
    /** The communication log vocabulary, read once for the whole table rather
     *  than joined per row. */
    labelSet?: Snapshot<LabelSet>;
    onDelete?: (record: CommunicationRecord, deleteAttachments: boolean) => void;
    onEmailSave?: (record: CommunicationRecord, data: EmailFormData) => boolean | Promise<boolean>;
    onPhoneSave?: (record: CommunicationRecord, data: PhoneCallFormData) => boolean | Promise<boolean>;
    onLabelsSave?: (record: CommunicationRecord, labelIds: LabelId[]) => boolean | Promise<boolean>;
    /** Asks Signify what it knows about an unresolved order attempt. Resolves to
     *  `undefined` when the check could not be run at all, which the caller has
     *  already reported. */
    onCheckOrderStatus?: (record: CommunicationRecord) => Promise<SignifyOrderStatusCheck | undefined>;
}

const RenderActions: FC<RenderActionsProps> = ({
    record,
    disabled = false,
    labelSet,
    onDelete,
    onEmailSave,
    onPhoneSave,
    onLabelsSave,
    onCheckOrderStatus,
}) => {
    const [shouldDeleteAttachments, setShouldDeleteAttachments] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [checking, setChecking] = useState(false);
    const [check, setCheck] = useState<SignifyOrderStatusCheck | undefined>(undefined);
    const [reportOpen, setReportOpen] = useState(false);

    const checkDisabledReason = !onCheckOrderStatus
        ? 'Checking with Signify is not available for this log.'
        : record.type !== 'api' || record.endpoint !== 'createOrder'
            ? 'Only an order submitted to Signify can be checked.'
            : record.outcome !== 'pending'
                ? 'This entry already records what Signify answered, so there is nothing to settle.'
                : undefined;

    const handleCheckOrderStatus = async () => {
        if (!onCheckOrderStatus) return;
        setChecking(true);
        try {
            const result = await onCheckOrderStatus(record);
            setCheck(result);
            setReportOpen(Boolean(result));
        } finally {
            setChecking(false);
        }
    };

    const handleConfirm = () => {
        const deleteAttachments = record.type === 'email'
            ? record.hasAttachments && shouldDeleteAttachments
            : false;
        onDelete?.(record, deleteAttachments);
        setShouldDeleteAttachments(false);
    };

    const handleCancel = () => {
        setShouldDeleteAttachments(false);
    };

    const handleEditSave = async (data: any) => {
        let success = true;
        if (record.type === 'email' && onEmailSave) {
            success = await onEmailSave(record, data);
        } else if (record.type === 'phone' && onPhoneSave) {
            success = await onPhoneSave(record, data);
        }
        if (success) {
            setEditOpen(false);
        }
        return success;
    };

    const isEmailWithAttachments = record.type === 'email' && record.hasAttachments;

    const canEdit = record.type === 'email' || record.type === 'phone';

    const editDisabledReason = !canEdit
        ? 'This kind of entry is written by the system and cannot be edited.'
        : disabled
            ? 'This log is read-only, so entries cannot be edited.'
            : undefined;

    const deleteDisabledReason = disabled
        ? 'This log is read-only, so entries cannot be deleted.'
        : undefined;

    const editContent = record.type === 'email' ? (
        <EmailPopoverForm
            initialValues={record}
            onSave={handleEditSave}
            onCancel={() => setEditOpen(false)}
            disabled={disabled}
        />
    ) : record.type === 'phone' ? (
        <PhoneCallPopoverForm
            initialValues={record}
            onSave={handleEditSave}
            onCancel={() => setEditOpen(false)}
            disabled={disabled}
        />
    ) : null;

    const deleteTypeLabel = record.type === 'phone'
        ? 'phone call'
        : record.type === 'api'
            ? 'API log entry'
            : record.type === 'attachment'
                ? 'rename log entry'
            : 'email';

    return (
        <FlexBox justifyEnd gap={2} stretch className='no-print'>
            <CommunicationLabelPicker
                record={record}
                set={labelSet}
                disabled={disabled}
                onSave={onLabelsSave}
            />
            <Tooltip title={checkDisabledReason}>
                <span>
                    <Button
                        type="text"
                        icon={<SearchOutlined />}
                        loading={checking}
                        disabled={Boolean(checkDisabledReason)}
                        onClick={() => { void handleCheckOrderStatus(); }}
                        aria-label={`Check Signify order status for log entry ${record.id}`}
                    />
                </span>
            </Tooltip>
            <Modal
                open={reportOpen}
                onCancel={() => setReportOpen(false)}
                onOk={() => setReportOpen(false)}
                okText="Close"
                okButtonProps={{ 'aria-label': `Close Signify order status for log entry ${record.id}` }}
                footer={(_, { OkBtn }) => <OkBtn />}
                title="Signify order status"
                destroyOnHidden
            >
                {check && <SignifyOrderStatusReport check={check} />}
            </Modal>

            {editDisabledReason ? (
                <Tooltip title={editDisabledReason}>
                    <span>
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            disabled
                            aria-label={`Edit communication entry ${record.id}`}
                        />
                    </span>
                </Tooltip>
            ) : (
                <Popover
                    content={editContent}
                    trigger="click"
                    open={editOpen}
                    onOpenChange={setEditOpen}
                    placement="bottomRight"
                    destroyOnHidden
                >
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        aria-label={`Edit communication entry ${record.id}`}
                    />
                </Popover>
            )}

            {deleteDisabledReason ? (
                <Tooltip title={deleteDisabledReason}>
                    <span>
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            disabled
                            aria-label={`Delete communication entry ${record.id}`}
                        />
                    </span>
                </Tooltip>
            ) : (
                <Popconfirm
                    icon={<QuestionCircleOutlined />}
                    title={`Delete ${deleteTypeLabel}?`}
                    description={
                        isEmailWithAttachments ? (
                            <FlexCol>
                                <span>Are you sure you want to delete this email?</span>
                                <Checkbox
                                    checked={shouldDeleteAttachments}
                                    onChange={(e) => setShouldDeleteAttachments(e.target.checked)}
                                    aria-label="Also delete attachments stored in this form"
                                >
                                    Also delete attachments from the form
                                </Checkbox>
                            </FlexCol>
                        ) : (
                            `Are you sure you want to delete this ${deleteTypeLabel}?`
                        )
                    }
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                    okText="Delete"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true, 'aria-label': 'Confirm delete communication' }}
                    cancelButtonProps={{ 'aria-label': 'Cancel delete communication' }}
                >
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label={`Delete communication entry ${record.id}`}
                    />
                </Popconfirm>
            )}
        </FlexBox>
    );
};

export default RenderActions;
